/**
 * IMPLEMENT-10 — Aussagebasierte visuelle Lernbilder für Gefahrenkarten.
 *
 * Ablauf: D1-Aussage (hazard.description) -> passendes visuelles Template -> SVG-Skizze.
 * Die Beschreibung ist maßgeblich; die Kategorie dient NUR als Fallback. Deterministisch,
 * keine KI, keine neuen Inhalte, keine Interpretation/Umformulierung. Es wird das ZUERST
 * genannte erkennbare Phänomen illustriert (bzw. eine eindeutige Kombination). Fail-closed:
 * Beschreibung -> Kategorie -> bestehendes Gefahrensymbol (null).
 *
 * Aufbau nur aus SVG-Primitiven (Linien, Pfeile, Kreise, Rechtecke, Bögen) und neutralen
 * Symbol-Glyphen (Hand, Person als Strichsymbol ohne Identität, Fuß, Ohr). Monochrom (slate)
 * + dezenter Amber-Akzent. Wie technische Unterweisungs-Skizzen: ruhig, klar, reduziert,
 * BG-tauglich. Keine Fotos/3D/Comic/Baustellenszenen.
 */

const SVG_PROPS = {
  viewBox: "0 0 80 48",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

// --- Neutrale Symbol-Glyphen (ohne Identität) ---
const HAND_D =
  "M0 8 v6 a3 3 0 0 0 3 3 h7 a3 3 0 0 0 3 -3 V6 M3 8 V4 M6.3 7 V1 M9.6 7 V2 M13 8 V5";
const Hand = ({ x, y, s = 0.85, flip = false }) => (
  <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
    <path d={HAND_D} />
  </g>
);
const Person = ({ x, y, s = 0.75 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <circle cx="0" cy="0" r="3.4" />
    <path d="M0 3.4 V14 M-5.5 7.5 H5.5 M0 14 L-4.5 23 M0 14 L4.5 23" />
  </g>
);
const Foot = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M2 2 V9 q0 1 1 1 H17 a3 3 0 0 0 3 -3 q0 -2 -4 -3 L9 4 q-1 -2 -3 -2 Z" />
  </g>
);
const Ear = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M3 2 a7 8 0 0 1 0 14 q-3 0 -3 -3 a2.5 2.5 0 0 0 -2 -3" />
  </g>
);

// 1a. Schnitt MIT Scheibe (Winkelschleifer): rotierende Scheibe + Hand + Schnittfunken.
function SchnittScheibeArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <circle cx="26" cy="22" r="12" />
      <circle cx="26" cy="22" r="2.2" fill="currentColor" stroke="none" />
      <path className="stroke-amber-500" d="M26 8 A14 14 0 0 1 38 16" />
      <path className="stroke-amber-500" d="M38 16 L39 10 M38 16 L32 16" />
      <Hand x={44} y={24} s={0.8} />
      <path className="stroke-amber-500" d="M40 23 l5 -2 M41 27 l5 -1" />
    </svg>
  );
}

// 1b. Schnitt OHNE Scheibe (Glaskante/Klinge): Klinge + Hand + Schnittfunken.
function CutArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M10 30 L40 14 L40 22 L16 34 Z" />
      <Hand x={46} y={22} s={0.85} />
      <path className="stroke-amber-500" d="M42 21 l5 -2 M43 25 l5 -1" />
    </svg>
  );
}

// Rückschlag / Verkanten: Scheibe + kräftiger Rückstoßpfeil.
function RueckschlagArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <circle cx="50" cy="24" r="12" />
      <circle cx="50" cy="24" r="2" fill="currentColor" stroke="none" />
      <path className="stroke-amber-500" d="M36 24 H10" />
      <path className="stroke-amber-500" d="M18 17 L10 24 L18 31" />
    </svg>
  );
}

// Wegfliegende Bruchstücke / Scheibenbruch / Splitter: Zentrum + radiale Splitterpfeile.
function BruchstueckeArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <circle cx="40" cy="24" r="7" />
      <path className="stroke-amber-500" d="M40 14 V6 M37 9 L40 5 L43 9" />
      <path className="stroke-amber-500" d="M50 20 L60 14 M55 12 L61 14 L59 20" />
      <path className="stroke-amber-500" d="M50 28 L60 34 M59 28 L61 34 L55 36" />
      <path className="stroke-amber-500" d="M30 20 L20 14 M25 12 L19 14 L21 20" />
    </svg>
  );
}

// Quetschen / Einsetzen schwerer Elemente: Objekt + zwei Hände + gegenläufige Pfeile.
function QuetschArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <rect x="35" y="18" width="10" height="12" rx="1.5" />
      <Hand x={6} y={16} s={0.7} />
      <Hand x={74} y={16} s={0.7} flip />
      <path className="stroke-amber-500" d="M22 24 H31 M27 21 L31 24 L27 27" />
      <path className="stroke-amber-500" d="M58 24 H49 M53 21 L49 24 L53 27" />
    </svg>
  );
}

// Absturz an Fensteröffnung: Fensterrahmen/Kante + Person + Pfeil nach unten.
function AbsturzFensterArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <rect x="10" y="6" width="22" height="28" rx="1.5" />
      <path d="M21 6 V34 M10 20 H32" />
      <Person x={45} y={11} s={0.7} />
      <path className="stroke-amber-500" d="M45 31 V42 M40 37 L45 43 L50 37" />
    </svg>
  );
}

// Absturz aus Höhe (generisch): Plattformkante + Person + Pfeil nach unten + Boden.
function AbsturzArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M6 18 H30" />
      <Person x={18} y={11} s={0.7} />
      <path className="stroke-amber-500" d="M52 12 V32 M46 26 L52 33 L58 26" />
      <path d="M6 42 H74" strokeDasharray="3 4" opacity="0.55" />
    </svg>
  );
}

// Stolpern / unebene Verkehrswege: Fuß + Hindernis + Stolperlinie.
function StolperArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M6 38 H30 L34 30 L42 30 L46 38 H74" />
      <Foot x={30} y={20} s={0.9} />
      <path className="stroke-amber-500" d="M52 12 L62 22 M62 22 L55 22 M62 22 L62 15" />
    </svg>
  );
}

// Umkippen / Wegrutschen / Leiter kippt: gekippte Leiter + Kipp-Pfeil + Boden.
function UmsturzArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M8 42 H72" />
      <g transform="rotate(22 24 42)">
        <path d="M20 12 V42 M30 12 V42 M20 20 H30 M20 28 H30 M20 36 H30" />
      </g>
      <path className="stroke-amber-500" d="M50 16 A16 16 0 0 1 61 30" />
      <path className="stroke-amber-500" d="M61 30 L61 23 M61 30 L54 29" />
    </svg>
  );
}

// Elektrisch / Spannung (generisch): unterbrochene Leitung + Blitz.
function ElektrischArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M6 24 H30" />
      <path d="M50 24 H74" />
      <path className="stroke-amber-500" d="M42 9 L33 26 H41 L35 39" />
    </svg>
  );
}

// Stromschlag bei beschädigtem Kabel: Kabel mit Bruchstelle + Blitz + Hand.
function StromschlagArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M6 30 H24 M28 30 q5 -9 11 -5" />
      <path className="stroke-amber-500" d="M44 12 L37 26 H45 L39 38" />
      <Hand x={54} y={20} s={0.8} />
    </svg>
  );
}

// Lärm / Schalldruck / Gehör: Schallwellen + Ohr.
function LaermArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <Ear x={14} y={15} s={1.1} />
      <path className="stroke-amber-500" d="M34 16 A14 14 0 0 1 34 32" />
      <path className="stroke-amber-500" d="M42 10 A22 22 0 0 1 42 38" opacity="0.7" />
      <path className="stroke-amber-500" d="M50 6 A28 28 0 0 1 50 42" opacity="0.5" />
    </svg>
  );
}

// Einatmen von Staub / Quarzstaub: Lunge + Staubpartikel.
function StaubLungeArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M40 12 V20 M36 20 C30 22 28 30 30 36 C32 40 38 39 38 33 V20 M44 20 C50 22 52 30 50 36 C48 40 42 39 42 33 V20" />
      <circle className="fill-amber-500" cx="16" cy="14" r="1.8" stroke="none" />
      <circle className="fill-amber-500" cx="22" cy="22" r="1.5" stroke="none" />
      <circle cx="14" cy="26" r="1.5" fill="currentColor" stroke="none" opacity="0.7" />
      <circle cx="62" cy="16" r="1.6" fill="currentColor" stroke="none" opacity="0.7" />
      <circle cx="60" cy="26" r="1.5" fill="currentColor" stroke="none" opacity="0.7" />
    </svg>
  );
}

// Vibration / Hand-Arm: Hand greift Werkzeug + Schwingungslinien.
function VibrationArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <rect x="30" y="20" width="22" height="10" rx="2" />
      <Hand x={18} y={16} s={0.8} />
      <path className="stroke-amber-500" d="M58 18 V32 M64 16 V34" />
      <path className="stroke-amber-500" d="M34 14 q4 -3 8 0" />
    </svg>
  );
}

// Heiß / Verbrennung / heiße Oberfläche: heiße Fläche + Hitzewellen + Hand.
function VerbrennungArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M10 38 H50" />
      <path d="M12 38 l4 4 M20 38 l4 4 M28 38 l4 4 M36 38 l4 4 M44 38 l4 4" opacity="0.6" />
      <path className="stroke-amber-500" d="M20 32 q-4 -6 0 -11 M30 32 q-4 -6 0 -11 M40 32 q-4 -6 0 -11" />
      <Hand x={56} y={14} s={0.8} />
    </svg>
  );
}

// Funkenflug / Funken: Quelle + abstrahlende Funken + kleine Flamme.
function SparkArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <circle cx="22" cy="28" r="4" />
      <path className="stroke-amber-500" d="M28 24 l9 -4 M29 28 l10 0 M28 32 l9 4" />
      <path className="stroke-amber-500" d="M58 36 C62 28 57 26 59 22 a6 6 0 1 1 -8 8 C50 28 55 30 53 36 Z" />
    </svg>
  );
}

// Brand / Feuer / Explosion / Entzündung: Flammenkontur.
function BrandArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path
        className="stroke-amber-500"
        d="M40 8 C46 18 52 22 48 31 a10 10 0 0 1 -19 1 C26 25 33 23 35 17 C37 21 41 19 40 8 Z"
      />
    </svg>
  );
}

// Heben / Tragen / Rücken / ergonomisch: Person + Last + Hebepfeil.
function ErgonomischArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <Person x={20} y={11} s={0.75} />
      <rect x="40" y="28" width="18" height="12" rx="2" />
      <path className="stroke-amber-500" d="M49 24 V12 M43 18 L49 12 L55 18" />
    </svg>
  );
}

// Rutsch / Glätte / Nässe / Witterung: schiefe Ebene + Rutschpfeil + Tropfen.
function RutschArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M8 16 L60 40" />
      <path className="stroke-amber-500" d="M28 22 L42 32 M42 32 L35 32 M42 32 L41 25" />
      <path d="M16 42 q2 3 4 0 M24 44 q2 3 4 0" opacity="0.55" />
    </svg>
  );
}

// Gefahrstoff / Chemie / Säure / ätzend: Kolben + Flüssigkeit.
function ChemieArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <path d="M34 8 H46 M36 8 V20 L28 38 a4 4 0 0 0 4 6 H48 a4 4 0 0 0 4 -6 L44 20 V8" />
      <path className="stroke-amber-500" d="M30 33 H50" />
    </svg>
  );
}

// Beschädigt / defekt / ungeeignet / Riss: Balken mit Riss.
function DefektArt({ className }) {
  return (
    <svg {...SVG_PROPS} className={className}>
      <rect x="30" y="8" width="12" height="32" rx="1.5" />
      <path className="stroke-amber-500" d="M30 24 L34 20 L38 27 L42 22" />
    </svg>
  );
}

// Prioritätsregeln (eindeutige Kombinationen) zuerst.
const PRIORITY_RULES = [
  {
    art: AbsturzFensterArt,
    test: (t) => /(absturz|abstürz|höhe|hoehe)/.test(t) && /(fenster|öffnung|oeffnung)/.test(t),
  },
];

// earliest-occurrence: das zuerst genannte erkennbare Phänomen bestimmt die Skizze.
const ART_RULES = [
  {
    keywords: ["schnittverletzung", "schnitt", "rotierende scheibe", "trennscheibe", "schleifscheibe", "glaskante", "scharfe kante", "scharfkant", "klinge", "schneid", "scharfe", "scheibe"],
    resolve: (t) => (/(scheibe|trennscheibe|schleifscheibe|rotierend)/.test(t) ? SchnittScheibeArt : CutArt),
  },
  { art: RueckschlagArt, keywords: ["rückschlag", "rueckschlag", "verkant", "zurückschlag", "kickback"] },
  { art: BruchstueckeArt, keywords: ["wegfliegend", "wegfliegen", "bruchstück", "bruchstueck", "scheibenbruch", "splitter", "umherfliegend", "herumfliegend", "abplatz"] },
  { art: QuetschArt, keywords: ["quetsch", "einklemm", "eingeklemmt", "klemm", "einzug", "einsetzen schwer"] },
  { art: StromschlagArt, keywords: ["stromschlag", "kabel"] },
  { art: ElektrischArt, keywords: ["elektr", "spannung", "stromführend", "spannungsführend", "strom"] },
  { art: AbsturzArt, keywords: ["absturz", "abstürz", "herabfall", "herunterfall", "herabstürz", "aus der höhe", "aus höhe", "höhe", "hoehe", "kante"] },
  { art: StolperArt, keywords: ["stolper", "unebene", "verkehrsweg"] },
  { art: UmsturzArt, keywords: ["umkipp", "umsturz", "kipp", "wegrutsch", "umfall", "instabil", "wackel"] },
  { art: RutschArt, keywords: ["ausrutsch", "rutsch", "glätte", "glaette", "nässe", "naesse", "glatt", "witterung"] },
  { art: StaubLungeArt, keywords: ["einatmen", "quarzstaub", "feinstaub", "staub", "atemweg", "lunge", "inhalation", "silikose"] },
  { art: LaermArt, keywords: ["lärm", "laerm", "schalldruck", "schall", "dezibel", "db(", "gehörschäd", "gehoerschaed", "gehörschad", "gehörverlust", "gehör"] },
  { art: VibrationArt, keywords: ["hand-arm-vibration", "hand-arm", "vibration", "durchblutungsstörung", "durchblutungsstoerung", "weißfinger", "weissfinger"] },
  { art: VerbrennungArt, keywords: ["verbrennung", "verbrüh", "heiße oberfläche", "heisse oberfläche", "heiße", "heisse", "heißen", "hitze", "glühend"] },
  { art: SparkArt, keywords: ["funkenflug", "funken", "funke"] },
  { art: BrandArt, keywords: ["brand", "feuer", "explos", "entzünd", "entzuend", "flamme"] },
  { art: ErgonomischArt, keywords: ["überkopf", "ueberkopf", "zwangshaltung", "rückenschäd", "rückenschad", "rücken", "ruecken", "heben", "tragen", "muskel-skelett", "muskel", "schwere last", "schwerer last", "lastenhandhabung"] },
  { art: ChemieArt, keywords: ["gefahrstoff", "chemi", "säure", "saeure", "lauge", "ätzend", "aetzend", "reizend", "toxisch", "gift", "gefahrgut", "dämpfe", "daempfe", "lösungsmittel"] },
  { art: DefektArt, keywords: ["beschädigt", "beschaedigt", "defekt", "ungeeignet", "mangelhaft", "riss", "gebrochen", "abgenutzt", "verschlissen"] },
];

function matchArt(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const t = value.toLowerCase();
  for (const r of PRIORITY_RULES) {
    if (r.test(t)) return r.art;
  }
  let best = null;
  let bestIdx = Infinity;
  for (const rule of ART_RULES) {
    for (const k of rule.keywords) {
      const i = t.indexOf(k);
      if (i !== -1 && i < bestIdx) {
        bestIdx = i;
        best = rule;
      }
    }
  }
  if (!best) return null;
  return best.resolve ? best.resolve(t) : best.art;
}

// Fail-closed-Kette: Beschreibung -> Kategorie -> null (bestehendes Icon).
function resolveHazardArt(description, category) {
  return matchArt(description) || matchArt(category) || null;
}

export function HazardIllustration({ text, category, testId }) {
  const Art = resolveHazardArt(text, category);
  if (!Art) return null;
  return (
    <div
      className="mb-4 flex h-20 items-center justify-center rounded-lg border border-amber-100 bg-white/70"
      data-testid={testId}
    >
      <Art className="h-12 w-auto text-slate-500" />
    </div>
  );
}
